import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { topicsApi, Topic } from '@/services/api';
import { useAuthStore } from '@/modules/auth/stores/authStore';
import { getLoginUrl } from '@/modules/auth/services/authService';

type Stance = 'pro' | 'con' | null;
type TopicStances = Record<string, Stance>;

export default function SelectTopicsPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isGuest } = useAuthStore();
  const [topics, setTopics] = useState<Topic[]>([]);
  const [stances, setStances] = useState<TopicStances>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 게스트는 이 페이지 접근 불가 - 로그인 페이지로 리다이렉트
  useEffect(() => {
    if (isGuest || !isAuthenticated) {
      alert('토론에 참여하려면 로그인이 필요합니다.');
      window.location.href = getLoginUrl();
    }
  }, [isGuest, isAuthenticated]);

  // 주제 로드
  useEffect(() => {
    async function loadTopics() {
      try {
        setIsLoading(true);
        const data = await topicsApi.getTopics(4);
        setTopics(data.topics);
      } catch (err) {
        console.error('Failed to load topics:', err);
        setError('주제를 불러오는데 실패했습니다');
      } finally {
        setIsLoading(false);
      }
    }
    loadTopics();
  }, []);

  const currentTopic = topics[currentIndex];
  const isLastTopic = currentIndex === topics.length - 1;
  const selectedCount = Object.values(stances).filter(Boolean).length;

  const handleSelectStance = (stance: Stance) => {
    if (!currentTopic) return;

    const newStances = { ...stances, [currentTopic.topicId]: stance };
    setStances(newStances);

    // 다음 주제로 자동 이동 또는 매칭 시작
    if (isLastTopic) {
      // 모든 주제 선택 완료 → 매칭 시작
      setTimeout(() => {
        navigate('/matching', {
          state: { stances: newStances, topics },
        });
      }, 300);
    } else {
      // 다음 주제로
      setTimeout(() => {
        setCurrentIndex((prev) => prev + 1);
      }, 300);
    }
  };

  const handleSkip = () => {
    if (isLastTopic) {
      if (selectedCount > 0) {
        navigate('/matching', { state: { stances, topics } });
      } else {
        // 하나도 선택 안 했으면 로비로
        navigate('/lobby');
      }
    } else {
      setCurrentIndex((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
    } else {
      navigate(-1);
    }
  };

  // 로딩 상태
  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-slate-400">주제를 불러오는 중...</p>
      </div>
    );
  }

  // 에러 상태
  if (error || topics.length === 0) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center p-4">
        <div className="text-6xl mb-6">😢</div>
        <h2 className="text-xl font-semibold mb-2">오류 발생</h2>
        <p className="text-slate-400 text-center mb-6">
          {error || '주제를 찾을 수 없습니다'}
        </p>
        <button onClick={() => navigate('/lobby')} className="btn-primary">
          로비로 돌아가기
        </button>
      </div>
    );
  }

  if (!currentTopic) return null;

  return (
    <div className="min-h-screen flex flex-col p-4 max-w-lg mx-auto">
      {/* Header */}
      <header className="flex items-center justify-between mb-6">
        <button onClick={handleBack} className="text-slate-400 hover:text-white p-2">
          ← 뒤로
        </button>
        <div className="text-center">
          <p className="text-sm text-slate-400">
            {currentIndex + 1} / {topics.length}
          </p>
        </div>
        <button onClick={handleSkip} className="text-slate-400 hover:text-white p-2 text-sm">
          건너뛰기
        </button>
      </header>

      {/* Progress Bar */}
      <div className="h-1 bg-slate-700 rounded-full mb-8">
        <div
          className="h-full bg-primary-500 rounded-full transition-all duration-300"
          style={{ width: `${((currentIndex + 1) / topics.length) * 100}%` }}
        />
      </div>

      {/* Topic Card */}
      <div className="flex-1 flex flex-col items-center justify-center">
        <div className="text-6xl mb-6">{currentTopic.emoji}</div>
        <h2 className="text-xl font-bold text-center leading-relaxed mb-4">
          {currentTopic.title}
        </h2>
        
        {/* 통계 표시 */}
        {currentTopic.stats.totalDebates > 0 && (
          <div className="flex items-center gap-4 text-sm text-slate-400 mb-6">
            <span>토론 {currentTopic.stats.totalDebates}회</span>
            <span>•</span>
            <span>
              찬성 {currentTopic.stats.proWins} vs 반대 {currentTopic.stats.conWins}
            </span>
          </div>
        )}

        {/* Stance Selection */}
        <div className="w-full space-y-4 mt-4">
          <button
            onClick={() => handleSelectStance('pro')}
            className={`w-full p-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-3
              ${stances[currentTopic.topicId] === 'pro'
                ? 'border-green-500 bg-green-500/20 scale-105'
                : 'border-slate-600 hover:border-green-500/50 hover:bg-green-500/10'
              }`}
          >
            <span className="text-3xl">👍</span>
            <span className="text-lg font-semibold">찬성</span>
          </button>

          <button
            onClick={() => handleSelectStance('con')}
            className={`w-full p-5 rounded-xl border-2 transition-all duration-200 flex items-center justify-center gap-3
              ${stances[currentTopic.topicId] === 'con'
                ? 'border-red-500 bg-red-500/20 scale-105'
                : 'border-slate-600 hover:border-red-500/50 hover:bg-red-500/10'
              }`}
          >
            <span className="text-3xl">👎</span>
            <span className="text-lg font-semibold">반대</span>
          </button>
        </div>
      </div>

      {/* Footer Info */}
      <footer className="mt-8 text-center">
        <p className="text-slate-500 text-sm">
          {selectedCount}개 주제 선택됨
        </p>
        {selectedCount >= 1 && !isLastTopic && (
          <button
            onClick={() => navigate('/matching', { state: { stances, topics } })}
            className="mt-4 text-primary-400 hover:underline text-sm"
          >
            지금 바로 매칭 시작하기 →
          </button>
        )}
      </footer>
    </div>
  );
}
